import { ScramjetClient } from "@client/index";
import { Tap } from "@/Tap";
import { iswindow } from "@client/entry";
import { SCRAMJETCLIENT } from "@/symbols";
import {
	Reflect_apply,
	Object_setPrototypeOf,
	_URL,
	Object_defineProperty,
} from "@/shared/snapshot";

export function createLocationProxy(client: ScramjetClient, self: GlobalThis) {
	const Location = iswindow ? self.Location : self.WorkerLocation;
	// location cannot be Proxy()d
	const fakeLocation: any = {};
	Object_setPrototypeOf(fakeLocation, Location.prototype);
	fakeLocation.constructor = Location;

	// for some reason it's on the object for Location and on the prototype for WorkerLocation??
	const descriptorSource = iswindow ? self.location : Location.prototype;
	const urlprops = [
		"protocol",
		"hash",
		"host",
		"hostname",
		"href",
		"origin",
		"pathname",
		"port",
		"search",
	];
	for (const prop of urlprops) {
		const native = client.natives.call(
			"Object.getOwnPropertyDescriptor",
			null,
			descriptorSource,
			prop
		);
		if (!native) continue;

		const desc: Partial<PropertyDescriptor> = {
			configurable: false,
			enumerable: true,
		};
		if (native.get) {
			desc.get = new Proxy(native.get, {
				apply() {
					return client.url[prop];
				},
			});
		}
		if (native.set) {
			desc.set = new Proxy(native.set, {
				apply(target, that, args) {
					if (prop === "href") {
						// special case
						client.url = args[0];

						return;
					}
					if (prop === "hash") {
						self.location.hash = args[0];
						Tap.dispatch(
							client.hooks.lifecycle.navigate,
							{
								type: "hashchange",
							},
							{
								url: client.url.href,
							}
						);

						return;
					}
					const url = new _URL(client.url.href);
					url[prop] = args[0];
					client.url = url;
				},
			});
		}
		Object_defineProperty(fakeLocation, prop, desc);
	}

	// functions
	fakeLocation.toString = new Proxy(self.location.toString, {
		apply() {
			return client.url.href;
		},
	});

	if (self.location.valueOf)
		fakeLocation.valueOf = new Proxy(self.location.valueOf, {
			apply() {
				return fakeLocation;
			},
		});
	if (self.location.assign)
		fakeLocation.assign = new Proxy(self.location.assign, {
			apply(target, that, args) {
				client.checkNavigation();
				args[0] = client.rewriteUrl(args[0]);
				Reflect_apply(target, self.location, args);
				Tap.dispatch(
					client.hooks.lifecycle.navigate,
					{
						type: "location",
					},
					{
						url: client.url.href,
					}
				);
			},
		});
	if (self.location.reload)
		fakeLocation.reload = new Proxy(self.location.reload, {
			apply(target, that, args) {
				Reflect_apply(target, self.location, args);
			},
		});
	if (self.location.replace)
		fakeLocation.replace = new Proxy(self.location.replace, {
			apply(target, that, args) {
				client.checkNavigation();
				args[0] = client.rewriteUrl(args[0]);
				Reflect_apply(target, self.location, args);

				Tap.dispatch(
					client.hooks.lifecycle.navigate,
					{
						type: "location",
					},
					{
						url: client.url.href,
					}
				);
			},
		});

	// ancestorOrigins: the origins of the frames above this one, nearest first, as the site sees
	// them. The page the proxy runs in is not one of them: the site's top page has an empty
	// list, like a page in its own tab. It was missing entirely, and ad and video scripts
	// that read it to learn whether they are framed stopped with a TypeError.
	if (iswindow && "ancestorOrigins" in self.location && self.DOMStringList) {
		Object_defineProperty(fakeLocation, "ancestorOrigins", {
			configurable: false,
			enumerable: true,
			get() {
				const origins: string[] = [];
				let current: any = self;
				for (;;) {
					let parent: any;
					try {
						parent = current.parent;
						if (parent === current || !(SCRAMJETCLIENT in parent)) break;
						origins.push(parent[SCRAMJETCLIENT].url.origin);
					} catch {
						break;
					}
					current = parent;
				}
				const list: any = {};
				Object_setPrototypeOf(list, self.DOMStringList.prototype);
				origins.forEach((origin, i) => {
					Object_defineProperty(list, i, { value: origin, enumerable: true });
				});
				Object_defineProperty(list, "length", { value: origins.length });
				Object_defineProperty(list, "item", {
					value: (i: number) => (i >= 0 && i < origins.length ? origins[i] : null),
				});
				Object_defineProperty(list, "contains", {
					value: (s: string) => origins.includes(String(s)),
				});

				return list;
			},
		});
	}

	return fakeLocation;
}
